import React, { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  DocumentTextIcon,
  PlayIcon,
  ClockIcon,
  EyeIcon,
  HeartIcon,
  LanguageIcon,
  CalendarIcon,
  MagnifyingGlassIcon,
  BookOpenIcon,
  LightBulbIcon,
  StarIcon as StarSolidIcon,
  CodeBracketIcon,
} from "@heroicons/react/24/outline";
import { useBlogs } from "../../contexts/BlogContext";

const BlogsPage: React.FC = () => {
  const { blogs, featuredBlogs, blogCategories, loading, error } = useBlogs();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [selectedLanguage, setSelectedLanguage] = useState<string>("all");
  const [selectedDifficulty, setSelectedDifficulty] = useState<string>("all");
  const [sortBy, setSortBy] = useState<"date" | "views" | "likes" | "title">(
    "date"
  );

  const languageOptions = ["Bengali", "English"];
  const difficultyOptions = ["Beginner", "Intermediate", "Advanced"];

  // Filter and sort blogs
  const filteredBlogs = blogs
    .filter((blog) => blog.published) // Only show published blogs
    .filter((blog) => {
      const searchMatch =
        searchTerm === "" ||
        blog.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        blog.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
        blog.tags.some((tag) =>
          tag.toLowerCase().includes(searchTerm.toLowerCase())
        ) ||
        blog.seoKeywords.some((keyword) =>
          keyword.toLowerCase().includes(searchTerm.toLowerCase())
        );

      const categoryMatch =
        selectedCategory === "all" || blog.category === selectedCategory;
      const languageMatch =
        selectedLanguage === "all" || blog.language === selectedLanguage;
      const difficultyMatch =
        selectedDifficulty === "all" || blog.difficulty === selectedDifficulty;

      return searchMatch && categoryMatch && languageMatch && difficultyMatch;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case "title":
          return a.title.localeCompare(b.title);
        case "views":
          return (b.views || 0) - (a.views || 0);
        case "likes":
          return (b.likes || 0) - (a.likes || 0);
        case "date":
        default:
          return (
            new Date(b.publishedDate || 0).getTime() -
            new Date(a.publishedDate || 0).getTime()
          );
      }
    });

  const publishedBlogs = blogs.filter((blog) => blog.published);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
      },
    },
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case "Beginner":
        return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
      case "Intermediate":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200";
      case "Advanced":
        return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200";
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + "M";
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + "K";
    }
    return num.toString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-purple-50 dark:from-gray-900 dark:via-green-900 dark:to-blue-900">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-green-600"></div>
        </div>
      </div>
    );
  }

  if (error || blogs.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-purple-50 dark:from-gray-900 dark:via-green-900 dark:to-blue-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            <div className="mx-auto h-32 w-32 text-green-400 mb-8">
              <DocumentTextIcon className="h-full w-full" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Content Coming Soon!
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-400 mb-8 max-w-2xl mx-auto">
              I'm crafting comprehensive coding tutorials and tech articles in
              both Bengali and English. Get ready for in-depth guides with
              YouTube videos and practical examples!
            </p>
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6 mb-8">
              <h3 className="text-lg font-semibold text-green-900 dark:text-green-300 mb-2">
                📚 Content Topics in Pipeline
              </h3>
              <ul className="text-left text-green-800 dark:text-green-400 space-y-2">
                <li>• Flutter state management deep dives</li>
                <li>• React performance optimization</li>
                <li>• Node.js API development tutorials</li>
                <li>• Mobile app deployment guides</li>
              </ul>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://github.com/taruncse"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-green-600 hover:bg-green-700 transition-colors"
              >
                <CodeBracketIcon className="h-5 w-5 mr-2" />
                Follow on GitHub
              </a>
              <Link
                to="/contact"
                className="inline-flex items-center px-6 py-3 border border-gray-300 dark:border-gray-600 text-base font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Suggest a Topic
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-blue-50 to-cyan-50 dark:from-gray-900 dark:via-indigo-900 dark:to-blue-900">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-indigo-600 to-blue-600 opacity-90"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Coding Tutorials
              <span className="bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">
                {" "}
                & Tech Articles
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-indigo-100 mb-8 max-w-3xl mx-auto">
              Learn programming through comprehensive tutorials, insightful
              articles, and real-world examples
            </p>
            <div className="flex flex-wrap justify-center gap-6 text-indigo-100">
              <div className="flex items-center space-x-2">
                <DocumentTextIcon className="h-6 w-6" />
                <span className="text-lg font-medium">
                  {publishedBlogs.length} Articles
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <PlayIcon className="h-6 w-6" />
                <span className="text-lg font-medium">
                  {publishedBlogs.filter((blog) => blog.youtubeVideoId).length}{" "}
                  Videos
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <EyeIcon className="h-6 w-6" />
                <span className="text-lg font-medium">
                  {formatNumber(
                    publishedBlogs.reduce(
                      (total, blog) => total + (blog.views || 0),
                      0
                    )
                  )}{" "}
                  Views
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <LanguageIcon className="h-6 w-6" />
                <span className="text-lg font-medium">Bengali & English</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Featured Blogs */}
      {featuredBlogs.length > 0 && (
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                ⭐ Featured Content
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-300">
                Most popular and comprehensive articles and tutorials
              </p>
            </motion.div>

            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            >
              {featuredBlogs.slice(0, 6).map((blog) => (
                <motion.div
                  key={blog.id}
                  variants={itemVariants}
                  whileHover={{ y: -10, scale: 1.02 }}
                  className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden border border-gray-200 dark:border-gray-700"
                >
                  <Link to={`/blogs/${blog.slug}`} className="block">
                    {/* Blog Image */}
                    <div className="relative h-48 overflow-hidden">
                      {blog.featuredImage ? (
                        <img
                          src={blog.featuredImage}
                          alt={blog.title}
                          className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center">
                          <DocumentTextIcon className="h-16 w-16 text-white" />
                        </div>
                      )}

                      {/* Video Indicator */}
                      {blog.youtubeVideoId && (
                        <div className="absolute top-4 left-4">
                          <div className="bg-red-600 text-white px-2 py-1 rounded-lg flex items-center space-x-1 text-sm font-medium">
                            <PlayIcon className="h-4 w-4" />
                            <span>Video</span>
                          </div>
                        </div>
                      )}

                      {/* Featured Badge */}
                      <div className="absolute top-4 right-4">
                        <StarSolidIcon className="h-6 w-6 text-yellow-400" />
                      </div>

                      {/* Reading Time */}
                      <div className="absolute bottom-4 left-4">
                        <div className="bg-black bg-opacity-70 text-white px-2 py-1 rounded-lg flex items-center space-x-1 text-sm">
                          <ClockIcon className="h-4 w-4" />
                          <span>{blog.readingTime} min read</span>
                        </div>
                      </div>
                    </div>

                    {/* Blog Content */}
                    <div className="p-6">
                      <div className="flex items-center space-x-2 mb-3">
                        <span className="text-sm font-medium text-indigo-600 dark:text-indigo-400">
                          {blog.category}
                        </span>
                        <span
                          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(
                            blog.difficulty
                          )}`}
                        >
                          {blog.difficulty}
                        </span>
                        <span className="inline-flex items-center text-xs text-gray-500 dark:text-gray-400">
                          <LanguageIcon className="h-3 w-3 mr-1" />
                          {blog.language}
                        </span>
                      </div>

                      <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3 line-clamp-2">
                        {blog.title}
                      </h3>

                      <p className="text-gray-600 dark:text-gray-300 mb-4 line-clamp-3">
                        {blog.excerpt}
                      </p>

                      {/* Tags */}
                      <div className="flex flex-wrap gap-2 mb-4">
                        {blog.tags.slice(0, 3).map((tag) => (
                          <span
                            key={tag}
                            className="inline-flex items-center px-2 py-1 rounded-md bg-indigo-100 dark:bg-indigo-900 text-indigo-800 dark:text-indigo-200 text-xs font-medium"
                          >
                            #{tag}
                          </span>
                        ))}
                        {blog.tags.length > 3 && (
                          <span className="inline-flex items-center px-2 py-1 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs">
                            +{blog.tags.length - 3}
                          </span>
                        )}
                      </div>

                      {/* Stats */}
                      <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
                        <div className="flex items-center space-x-4">
                          <div className="flex items-center space-x-1">
                            <EyeIcon className="h-4 w-4" />
                            <span>{formatNumber(blog.views || 0)}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <HeartIcon className="h-4 w-4" />
                            <span>{blog.likes || 0}</span>
                          </div>
                        </div>
                        <div className="flex items-center space-x-1">
                          <CalendarIcon className="h-4 w-4" />
                          <span>{formatDate(blog.publishedDate || "")}</span>
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>
      )}

      {/* All Blogs Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              All Content
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300">
              Comprehensive collection of programming tutorials, articles, and
              guides
            </p>
          </motion.div>

          {/* Filters */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 mb-8"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
              {/* Search */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Search Content
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="block w-full pl-10 pr-3 py-3 border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                    placeholder="Search articles and tutorials..."
                  />
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                </div>
              </div>

              {/* Category Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Category
                </label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="block w-full px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="all">All Categories</option>
                  {blogCategories.map((category) => (
                    <option key={category.id} value={category.name}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Language Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Language
                </label>
                <select
                  value={selectedLanguage}
                  onChange={(e) => setSelectedLanguage(e.target.value)}
                  className="block w-full px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="all">All Languages</option>
                  {languageOptions.map((language) => (
                    <option key={language} value={language}>
                      {language}
                    </option>
                  ))}
                </select>
              </div>

              {/* Difficulty Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Difficulty
                </label>
                <select
                  value={selectedDifficulty}
                  onChange={(e) => setSelectedDifficulty(e.target.value)}
                  className="block w-full px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="all">All Levels</option>
                  {difficultyOptions.map((difficulty) => (
                    <option key={difficulty} value={difficulty}>
                      {difficulty}
                    </option>
                  ))}
                </select>
              </div>

              {/* Sort */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Sort By
                </label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as any)}
                  className="block w-full px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="date">Latest</option>
                  <option value="views">Most Viewed</option>
                  <option value="likes">Most Liked</option>
                  <option value="title">Title A-Z</option>
                </select>
              </div>
            </div>
          </motion.div>

          {/* Blogs Grid */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {filteredBlogs.map((blog) => (
              <motion.div
                key={blog.id}
                variants={itemVariants}
                whileHover={{ y: -5, scale: 1.02 }}
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-200 dark:border-gray-700"
              >
                <Link to={`/blogs/${blog.slug}`} className="block">
                  {/* Blog Image */}
                  <div className="relative h-40 overflow-hidden">
                    {blog.featuredImage ? (
                      <img
                        src={blog.featuredImage}
                        alt={blog.title}
                        className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center">
                        <DocumentTextIcon className="h-12 w-12 text-white" />
                      </div>
                    )}

                    {/* Video Indicator */}
                    {blog.youtubeVideoId && (
                      <div className="absolute top-3 left-3">
                        <div className="bg-red-600 text-white px-2 py-1 rounded-md flex items-center space-x-1 text-xs font-medium">
                          <PlayIcon className="h-3 w-3" />
                          <span>Video</span>
                        </div>
                      </div>
                    )}

                    {/* Featured Badge */}
                    {blog.featured && (
                      <div className="absolute top-3 right-3">
                        <StarSolidIcon className="h-5 w-5 text-yellow-400" />
                      </div>
                    )}

                    {/* Reading Time */}
                    <div className="absolute bottom-3 left-3">
                      <div className="bg-black bg-opacity-70 text-white px-2 py-1 rounded-md flex items-center space-x-1 text-xs">
                        <ClockIcon className="h-3 w-3" />
                        <span>{blog.readingTime} min</span>
                      </div>
                    </div>
                  </div>

                  {/* Blog Content */}
                  <div className="p-6">
                    <div className="flex items-center space-x-2 mb-3">
                      <span className="text-sm font-medium text-indigo-600 dark:text-indigo-400">
                        {blog.category}
                      </span>
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(
                          blog.difficulty
                        )}`}
                      >
                        {blog.difficulty}
                      </span>
                    </div>

                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2 line-clamp-2">
                      {blog.title}
                    </h3>

                    <p className="text-gray-600 dark:text-gray-300 mb-4 line-clamp-2 text-sm">
                      {blog.excerpt}
                    </p>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {blog.tags.slice(0, 2).map((tag) => (
                        <span
                          key={tag}
                          className="inline-flex items-center px-2 py-1 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs"
                        >
                          #{tag}
                        </span>
                      ))}
                      {blog.tags.length > 2 && (
                        <span className="inline-flex items-center px-2 py-1 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-xs">
                          +{blog.tags.length - 2}
                        </span>
                      )}
                    </div>

                    {/* Stats */}
                    <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
                      <div className="flex items-center space-x-3">
                        <div className="flex items-center space-x-1">
                          <EyeIcon className="h-4 w-4" />
                          <span>{formatNumber(blog.views || 0)}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <HeartIcon className="h-4 w-4" />
                          <span>{blog.likes || 0}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <LanguageIcon className="h-4 w-4" />
                          <span>{blog.language}</span>
                        </div>
                      </div>
                      <span className="text-xs">
                        {formatDate(blog.publishedDate || "")}
                      </span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </motion.div>

          {filteredBlogs.length === 0 && (
            <div className="text-center py-12">
              <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No content found
              </h3>
              <p className="text-gray-500 dark:text-gray-400">
                Try adjusting your search terms or filters
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Categories Section */}
      {blogCategories.length > 0 && (
        <section className="py-16 bg-white/50 dark:bg-gray-800/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                Content Categories
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-300">
                Explore articles and tutorials by technology and topic
              </p>
            </motion.div>

            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6"
            >
              {blogCategories.map((category) => {
                return (
                  <motion.div
                    key={category.id}
                    variants={itemVariants}
                    whileHover={{ y: -5, scale: 1.05 }}
                    className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 text-center cursor-pointer"
                    onClick={() => setSelectedCategory(category.name)}
                  >
                    {category.icon ? (
                      <img
                        src={category.icon}
                        alt={category.name}
                        className="w-12 h-12 mx-auto mb-4"
                      />
                    ) : (
                      <div className="w-12 h-12 mx-auto mb-4 bg-gradient-to-br from-indigo-500 to-blue-600 rounded-lg flex items-center justify-center">
                        <BookOpenIcon className="h-6 w-6 text-white" />
                      </div>
                    )}
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                      {category.name}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
                      {category.description}
                    </p>
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-indigo-100 dark:bg-indigo-900 text-indigo-800 dark:text-indigo-200">
                      {
                        blogs.filter((blog) => blog.category === category.name)
                          .length
                      }{" "}
                      article
                      {blogs.filter((blog) => blog.category === category.name)
                        .length !== 1
                        ? "s"
                        : ""}
                    </span>
                  </motion.div>
                );
              })}
            </motion.div>
          </div>
        </section>
      )}

      {/* Newsletter Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-indigo-600 to-blue-600 rounded-2xl shadow-xl p-8 text-center"
          >
            <LightBulbIcon className="h-12 w-12 text-yellow-400 mx-auto mb-4" />
            <h2 className="text-3xl font-bold text-white mb-4">
              Stay Updated with Latest Content
            </h2>
            <p className="text-xl text-indigo-100 mb-8">
              Get notified when new coding tutorials, tech articles, and
              programming tips are published
            </p>
            <div className="max-w-md mx-auto flex">
              <input
                type="email"
                placeholder="Enter your email address"
                className="flex-1 px-4 py-3 rounded-l-xl border-0 focus:ring-2 focus:ring-yellow-400 focus:outline-none"
              />
              <button className="bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-bold py-3 px-6 rounded-r-xl transition-colors">
                Subscribe
              </button>
            </div>
            <p className="text-sm text-indigo-200 mt-4">
              No spam, unsubscribe anytime. Join 1000+ developers!
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default BlogsPage;
